import type React from 'react';

import { List as MuiList } from '@mui/material';
import { JsonSchemaInput } from '@ragu-components/json-schema-input';
import { OrdFormList, OrdIconStandard, OrdResourceForm, useDeleteProps } from '@ragu-components/ord-components';
import { TabbedForm } from 'react-admin';

import { OrdDescriptionInput } from 'src/components/formInputs';

import { CalendarEventsDateTimeForm } from './CalendarEventsDateTimeForm';
import { CalendarReferenceInput } from './CalendarReferenceInput';
import { CalendarEventsRecurrenceForm } from './recurrence';
import { buildSchema, buildFields } from './utils';

import type { CalendarEventsFormProps } from './CalendarEventsForm';
import type { CalendarEvent } from '@ragu-components/ord-types';
import type { Identifier } from 'react-admin';

interface CalendarEventsMainFormProps extends Omit<CalendarEventsFormProps, 'type' | 'isReadonly' | 'showId'> {
  isCreate: boolean;
  parentId?: string;
  setParentId: (id: string) => void;
  options: { meta: { parentResource?: string; parentId?: Identifier } };
}

export const CalendarEventsMainForm = ({
  isCreate,
  isPersonalResource = false,
  selectedCalendarIds,
  parentId = undefined,
  setParentId,
  options,
  ...props
}: CalendarEventsMainFormProps): React.ReactElement => {
  const jsonSchema = buildSchema(isCreate, isPersonalResource);
  const fields = buildFields(isCreate, isPersonalResource);
  const deleteProps = {
    ...useDeleteProps<CalendarEvent>((record) => record.title),
    mutationOptions: options,
  };

  return (
    <OrdResourceForm
      jsonSchema={jsonSchema}
      jsonSchemaOptions={{ removeAdditional: 'all' }}
      deleteProps={isCreate ? undefined : deleteProps}
      syncWithLocation={false}
      hideLabels
      {...props}
    >
      <TabbedForm.Tab label='resources.me/calendar_events.operations.sections.details'>
        <MuiList sx={{ width: '100%' }}>
          <OrdFormList icon={<OrdIconStandard name='shortText' />}>
            <JsonSchemaInput jsonSchema={jsonSchema} source={fields.title} />
            <OrdDescriptionInput source={fields.description} />
          </OrdFormList>
          <CalendarEventsDateTimeForm fields={fields} />
          <CalendarReferenceInput
            isCreate={isCreate}
            parentId={parentId}
            setParentId={setParentId}
            isPersonalResource={isPersonalResource}
            selectedCalendarIds={selectedCalendarIds}
          />
        </MuiList>
      </TabbedForm.Tab>
      <TabbedForm.Tab label='resources.me/calendar_events.operations.sections.advancedSettings'>
        <CalendarEventsRecurrenceForm parentFields={fields} />
      </TabbedForm.Tab>
    </OrdResourceForm>
  );
};
